import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SEO } from '@/components/SEO';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await api.post('/auth/forgot-password', null, { params: { email } });
      setSent(true);
      toast.success('Password reset email sent!');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      // Show generic message even for Google OAuth accounts for security
      if (typeof errorDetail === 'string' && errorDetail.includes('Google Sign-In')) {
        setError(errorDetail);
      } else {
        setSent(true); // Don't reveal if email exists
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" data-testid="forgot-password-page">
      <SEO 
        title="Forgot Password"
        description="Reset your password for Twenty Tulips Diaries."
        pathname="/forgot-password"
        noindex={true}
      />
      <div className="max-w-md w-full">
        {/* Back link */}
        <Link 
          to="/login" 
          className="inline-flex items-center text-sm text-stone-600 hover:text-stone-900 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Sign In
        </Link>
        
        {!sent ? (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <div className="mx-auto w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-7 w-7 text-stone-600" />
              </div>
              <h1 className="text-2xl md:text-3xl font-medium mb-2">Forgot your password?</h1>
              <p className="text-stone-600">
                No worries! Enter your email and we&apos;ll send you a reset link.
              </p>
            </div>
            
            {/* Form */}
            <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </div>
          </>
        ) : (
          <>
            {/* Success state */}
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl md:text-3xl font-medium mb-4">Check your email</h1>
              <p className="text-stone-600 mb-2">
                If an account exists with
              </p>
              <p className="font-medium text-stone-900 mb-6">{email}</p>
              <p className="text-sm text-stone-500 mb-8">
                you&apos;ll receive a password reset link shortly. Check your spam folder if you don&apos;t see it.
              </p>
              
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
